import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { FiAlertTriangle, FiCheck, FiRefreshCw } from "react-icons/fi";
import useCallCenterT from "../../hooks/useCallCenterT";
import {
  acknowledgeAlert,
  getAlertSummary,
  listAlerts,
} from "../../service/CallCenterAlerts";
import { isCallingEnabled } from "../../utils/orgFeatures";
import { processAgentAlertNotifications } from "../../utils/agentAlertNotifications";
import { formatAlertDuration } from "../../utils/alertDuration";

const POLL_MS = 30000;

const statusClass = (status) => {
  switch (status) {
    case "WARNING":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "ESCALATED_L1":
    case "ESCALATED_L2":
      return "bg-red-50 text-red-800 border-red-200";
    case "ACKNOWLEDGED":
      return "bg-blue-50 text-blue-800 border-blue-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
};

const alertLink = (alert) => {
  if (alert.itemType === "TICKET" && alert.referenceId) {
    const params = new URLSearchParams({ ticketId: alert.referenceId });
    if (alert.referenceNumber) {
      params.set("accountNo", alert.referenceNumber);
    }
    return `/call-center/tickets?${params.toString()}`;
  }
  if (alert.itemType === "BULK_ASSIGNMENT") {
    const params = new URLSearchParams();
    if (alert.agentUserId) {
      params.set("assigneeId", String(alert.agentUserId));
    }
    const query = params.toString();
    return query ? `/call-center/tickets?${query}` : "/call-center/tickets";
  }
  if (alert.itemType === "MISSED_CALL" && isCallingEnabled()) {
    return "/call-center/browser-phone";
  }
  return "/call-center/alerts";
};

const AgentAlertsPanel = ({ compact = false }) => {
  const t = useCallCenterT();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ackBusyId, setAckBusyId] = useState(null);
  const [ackTarget, setAckTarget] = useState(null);
  const [ackNote, setAckNote] = useState("");
  const currentUserId = Number(Cookies.get("userId") || 0);

  const load = useCallback(async () => {
    try {
      const [listData, summaryData] = await Promise.all([
        listAlerts({ page: 0, size: compact ? 5 : 10, openOnly: true }),
        getAlertSummary(),
      ]);
      const items = Array.isArray(listData?.items) ? listData.items : listData?.content || [];
      setAlerts(items);
      setSummary(summaryData);
      processAgentAlertNotifications(items, summaryData, t);
    } catch (error) {
      if (!compact) {
        toast.error(error.message || t("ccAlertsLoadFailed"));
      }
    } finally {
      setLoading(false);
    }
  }, [compact, t]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const submitAcknowledge = async () => {
    if (!ackTarget) return;
    try {
      setAckBusyId(ackTarget.id);
      await acknowledgeAlert(ackTarget.id, ackNote.trim() || undefined);
      toast.success(t("ccAlertAcknowledged"));
      setAckTarget(null);
      setAckNote("");
      await load();
    } catch (error) {
      toast.error(error.message || t("ccAlertAckFailed"));
    } finally {
      setAckBusyId(null);
    }
  };

  const openCount = summary?.openCount ?? summary?.open ?? 0;
  const escalatedCount = summary?.escalatedCount ?? summary?.escalated ?? 0;
  const attentionCount = summary?.attentionCount ?? summary?.attention ?? openCount;

  if (loading && alerts.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
        <p className="text-sm text-slate-500">{t("ccAlertsLoading")}</p>
      </section>
    );
  }

  if (openCount === 0 && alerts.length === 0) {
    return null;
  }

  return (
    <>
      <section className="mb-8 rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <FiAlertTriangle className={`h-5 w-5 ${escalatedCount > 0 ? "text-red-500" : "text-amber-500"}`} />
            <div>
              <h2 className="text-base font-semibold text-slate-800">{t("ccOperationalAlerts")}</h2>
              <p className="text-xs text-slate-500">
                {t("ccOperationalAlertsSubtitle")
                  .replace("{open}", String(openCount))
                  .replace("{escalated}", String(escalatedCount))}
                {attentionCount > 0 ? ` · ${attentionCount} need action` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
              aria-label={t("ccRefresh")}
            >
              <FiRefreshCw className="h-4 w-4" />
            </button>
            {!compact && (
              <Link
                to="/call-center/alerts"
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {t("ccViewAllAlerts")}
              </Link>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3 font-medium">{t("ccAlertAgent")}</th>
                <th className="px-5 py-3 font-medium">{t("ccAlertReference")}</th>
                <th className="px-5 py-3 font-medium">{t("ccAlertReason")}</th>
                <th className="px-5 py-3 font-medium">{t("ccAlertPriority")}</th>
                <th className="px-5 py-3 font-medium">{t("ccAlertOpenFor")}</th>
                <th className="px-5 py-3 font-medium">{t("ccAlertStatus")}</th>
                <th className="px-5 py-3 font-medium">{t("ccActions")}</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => {
                const canAck =
                  alert.agentUserId === currentUserId &&
                  alert.status !== "ACKNOWLEDGED" &&
                  alert.status !== "RESOLVED";
                return (
                  <tr key={alert.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-5 py-3 font-medium text-slate-800">{alert.agentName || "—"}</td>
                    <td className="px-5 py-3">
                      <Link to={alertLink(alert)} className="text-[#2B7FFF] hover:underline">
                        {alert.referenceNumber || alert.referenceId}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{alert.reason?.replace(/_/g, " ") || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{alert.priority || "—"}</td>
                    <td className="px-5 py-3 font-medium text-slate-700">
                      {formatAlertDuration(alert.pendingDurationSeconds)}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusClass(alert.status)}`}
                      >
                        {alert.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {canAck ? (
                        <button
                          type="button"
                          disabled={ackBusyId === alert.id}
                          onClick={() => {
                            setAckTarget(alert);
                            setAckNote("");
                          }}
                          className="inline-flex items-center gap-1 rounded-lg bg-[#2B7FFF] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1f6fe8] disabled:opacity-60"
                        >
                          <FiCheck className="h-3.5 w-3.5" />
                          {t("ccAcknowledge")}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {ackTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">{t("ccAcknowledgeAlert")}</h3>
            <p className="mt-1 text-sm text-slate-500">{ackTarget.referenceNumber}</p>
            <textarea
              value={ackNote}
              onChange={(e) => setAckNote(e.target.value)}
              rows={3}
              className="mt-4 w-full rounded-xl border border-slate-200 p-3 text-sm"
              placeholder={t("ccAckNotePlaceholder")}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAckTarget(null);
                  setAckNote("");
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm"
              >
                {t("ccCancel")}
              </button>
              <button
                type="button"
                onClick={submitAcknowledge}
                disabled={ackBusyId === ackTarget.id}
                className="rounded-xl bg-[#2B7FFF] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {t("ccAcknowledge")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentAlertsPanel;
